import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useRouteResolver } from '@experiences/util';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { CarouselSlideParameters } from './types/CarouselSlideParameters';

const MAESTRO_FLOW_SLIDE_ID = 'maestro-flow-launch';

/**
 * Deep link into Studio Web's new-project flow with a Maestro Flow project
 * preselected, rather than dropping the user on the Studio Web landing page.
 * This is the same route and query Studio Web's own "create project" cards
 * use: `designer/new` (its `routes.designerNew`) with `projectMode` set to the
 * `ProjectMode.Flow` enum value.
 */
const STUDIO_WEB_NEW_FLOW_PROJECT = '/designer/new?createSolution=true&projectMode=Flow&local=false';

/**
 * Maestro Automate is not a project mode of its own: it is the same
 * `projectMode=Flow` new-project flow plus a `maestroAutomate` flag that tells
 * Studio Web to open it in Automate mode.
 */
const STUDIO_WEB_NEW_AUTOMATE_PROJECT = `${STUDIO_WEB_NEW_FLOW_PROJECT}&maestroAutomate=true`;

/**
 * Launch slide for Maestro Flow and Maestro Automate, defined in code and gated by
 * `EnableMaestroFlowCarouselSlide` so it needs no Contentful entry. Rendered by
 * `MaestroFlowSlideLayout`. While the flag is on the carousel pins it
 * ahead of the Contentful slides; flipping the flag off retires it without a deploy.
 */
export const useMaestroFlowSlide = (): CarouselSlideParameters | undefined => {
    const enabled = useFeatureFlagValue(Features.EnableMaestroFlowCarouselSlide.name);
    const { formatMessage: translate } = useIntl();
    const getRoute = useRouteResolver();

    return useMemo(() => (enabled ? {
        id: MAESTRO_FLOW_SLIDE_ID,
        layout: 'maestroFlow',
        title: translate({ id: 'CLIENT_CAROUSEL_MAESTRO_FLOW_TITLE' }),
        description: translate({ id: 'CLIENT_CAROUSEL_MAESTRO_FLOW_DESCRIPTION' }),
        buttonText: translate({ id: 'CLIENT_CAROUSEL_MAESTRO_FLOW_BUTTON_FLOW' }),
        buttonRedirectUrl: `${getRoute('/:accountName/studio_')}${STUDIO_WEB_NEW_FLOW_PROJECT}`,
        secondaryButtonText: translate({ id: 'CLIENT_CAROUSEL_MAESTRO_FLOW_BUTTON_AUTOMATE' }),
        secondaryButtonRedirectUrl: `${getRoute('/:accountName/studio_')}${STUDIO_WEB_NEW_AUTOMATE_PROJECT}`,
    } : undefined), [ enabled, translate, getRoute ]);
};
